import { html, LitElement } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';

import clamp from '../../utils/clamp';

import styles from './progress-bar.styles';

export type ProgressBarVariant = 'default' | 'success' | 'warning' | 'danger';

export interface ProgressBarProps {
	variant?: ProgressBarVariant;
	value: number;
	max: number;
}

const DEFAULT_VARIANT = 'default';
const DEFAULT_MAX = 100;
const DEFAULT_MIN = 0;
const DEFAULT_VALUE = 0;

export class ProgressBar extends LitElement {
	static override styles = [styles()];

	static override get properties() {
		return {
			variant: { type: String, reflect: true },
			value: { type: Number, reflect: true },
			max: { type: Number, reflect: true },
		};
	}

	/**
	 * ProgressBar variant.
	 */
	declare variant: ProgressBarProps['variant'];

	/**
	 * Specifies how much of the task that has been completed.
	 */
	declare value: ProgressBarProps['value'];

	/**
	 * Describes how much work the task indicated by the progress element requires.
	 */
	declare max: ProgressBarProps['max'];

	constructor() {
		super();

		this.variant = DEFAULT_VARIANT;
		this.value = DEFAULT_VALUE;
		this.max = DEFAULT_MAX;
	}

	override connectedCallback(): void {
		super.connectedCallback();

		this.setAttribute('role', 'progressbar');
		this.setAttribute('aria-valuemin', '0');
	}

	protected override updated(): void {
		this.setAttribute('aria-valuenow', String(this.value));
		this.setAttribute('aria-valuemax', String(this.max));
	}

	get #safeMaxValue() {
		return this.max <= 0 ? DEFAULT_MAX : this.max;
	}

	get #safeValue() {
		return clamp(this.value, DEFAULT_MIN, this.#safeMaxValue);
	}

	get percentage() {
		return (this.#safeValue * 100) / this.#safeMaxValue;
	}

	protected get currentVariant() {
		return this.variant;
	}

	override render() {
		return html`
			<div class="bar">
				<span
					class=${classMap({
						progress: true,
						'-default': this.currentVariant === 'default',
						'-success': this.currentVariant === 'success',
						'-warning': this.currentVariant === 'warning',
						'-danger': this.currentVariant === 'danger',
					})}
					style=${styleMap({
						width: `${this.percentage}%`,
					})}
				></span>
			</div>
		`;
	}
}

window.customElements.define('m-progress-bar', ProgressBar);

declare global {
	interface HTMLElementTagNameMap {
		'm-progress-bar': ProgressBar;
	}
}
